import { useState } from 'react';
import { useTranslation } from '../../../../lib/i18n';
import { POST_PLAIN_MARKS, POST_STORY_RENDITION, postAuthorName, postMediaKey } from '../../../../lib/social-post-look-parts';
import { TIKTOK_CAPTION_LIMIT, tiktokDots, type TiktokLook } from '../../../../lib/tiktok-post-preview';
import {
  CarouselDots,
  POST_LOOK_KEYS,
  PostCaption,
  StepButtons,
  TallFrame,
  TallMedia,
  useMediaLabel,
  type SocialPostLookProps,
} from './SocialPostLookParts';
import styles from './TiktokPostPreview.module.css';

export function TiktokVideo(props: SocialPostLookProps) {
  return <TiktokPost key={postMediaKey(props.card)} look="tiktok-video" {...props} />;
}

export function TiktokPhoto(props: SocialPostLookProps) {
  return <TiktokPost key={postMediaKey(props.card)} look="tiktok-photo" {...props} />;
}

function TiktokPost({ card, look, onOpenMedia, onToggleCaption }: SocialPostLookProps & { look: TiktokLook }) {
  const { t } = useTranslation();
  const items = card.media;
  const label = useMediaLabel(card);
  const [index, setIndex] = useState(0);
  const [fits, setFits] = useState(false);
  const current = items[Math.min(index, items.length - 1)];
  if (!current) {
    return null;
  }
  const dots = tiktokDots(items.length, index);
  return (
    <div className={styles.frame} data-testid="tiktok-post" data-look={look}>
      <TallFrame fits={fits}>
        <TallMedia
          key={`${index}-${current.url}`}
          media={current}
          label={label(current, index)}
          rendition={POST_STORY_RENDITION}
          fit={current.kind === 'video' ? 'cover' : 'contain'}
          soundClassName={styles.sound}
          onOpen={() => onOpenMedia(current, index)}
          onFit={setFits}
        />
        <div className={styles.tabs} aria-hidden="true" data-testid="tiktok-tabs">
          <span className={styles.tab}>{t(POST_LOOK_KEYS.following)}</span>
          <span className={`${styles.tab} ${styles.tabActive}`}>{t(POST_LOOK_KEYS.forYou)}</span>
        </div>
        <div className={styles.overlay}>
          <span className={styles.name} data-testid="tiktok-account-name">
            <bdi>{postAuthorName(card)}</bdi>
          </span>
          {card.caption && (
            <PostCaption
              caption={card.caption}
              testId="tiktok-caption"
              className={styles.caption}
              cut={{ limit: TIKTOK_CAPTION_LIMIT }}
              marks={POST_PLAIN_MARKS}
              onMedia
              onToggle={onToggleCaption}
            />
          )}
        </div>
        {items.length > 1 && <CarouselDots count={dots.count} active={dots.active} className={styles.dots} />}
        <StepButtons index={index} total={items.length} size="small" light onStep={setIndex} />
      </TallFrame>
    </div>
  );
}
